<!--
  Feedback reports, for the deployment's operators: the list (newest
  first), or one report with its screenshot, logs and picked element, and
  a form to forward it to a GitHub issue.
-->
<script lang="ts">
  import { fetchScreenshot, forwardFeedback, getFeedback, issueDraft, listFeedback, type Report, type ReportSummary } from '../lib/api/feedback';
  import { toggleLine } from '../lib/feedback/issue';
  import { session } from '../lib/state/session.svelte';
  import LoadingGate from '../lib/loading/LoadingGate.svelte';
  import { oneStep } from '../lib/loading/steps';
  import { relTime } from '../lib/logic/time';
  import { imageSource } from '../lib/logic/preview';

  interface Props {
    /** One report; the list when absent. */
    id?: number;
  }
  let { id }: Props = $props();

  let reports: ReportSummary[] = $state([]);
  let more = $state(false);
  let report: Report | null = $state(null);
  let shot: string | null = $state(null);
  let loading = $state(true);
  let error = $state('');

  let repo = $state('');
  let linkShot = $state(false);
  let title = $state('');
  let body = $state('');
  /** The line that links the screenshot, as the server writes it; empty when there is none. */
  let shotLine = $state('');
  let forwarding = $state(false);
  let forwardError = $state('');

  async function loadList(before?: number) {
    const page = await listFeedback(before);
    reports = before ? [...reports, ...page.reports] : page.reports;
    more = page.more;
  }

  $effect(() => {
    const which = id;
    loading = true;
    error = '';
    report = null;
    let release: (() => void) | null = null;
    void (async () => {
      try {
        if (which === undefined) await loadList();
        else {
          report = await getFeedback(which);
          if (!report.forwarded) {
            const [plain, linked] = await Promise.all([issueDraft(which, false), report.screenshot ? issueDraft(which, true) : null]);
            title = plain.title;
            body = plain.body;
            linkShot = false;
            shotLine = linked ? linked.body.slice(plain.body.length) : '';
          }
          if (report.screenshot) {
            const { bytes, type } = await fetchScreenshot(which);
            const source = imageSource(bytes, type);
            release = source.release;
            shot = source.url;
          }
        }
      } catch (e) {
        error = e instanceof Error ? e.message : String(e);
      } finally {
        loading = false;
      }
    })();
    return () => {
      release?.();
      shot = null;
    };
  });

  async function forward(e: SubmitEvent) {
    e.preventDefault();
    if (!report) return;
    forwarding = true;
    forwardError = '';
    try {
      const { issue } = await forwardFeedback(report.id, repo.trim(), { title, body });
      report = { ...report, forwarded: { url: issue.url, at: new Date().toISOString() } };
    } catch (err) {
      forwardError = err instanceof Error ? err.message : String(err);
    } finally {
      forwarding = false;
    }
  }

  const when = (iso: string) => `${relTime(iso)} · ${new Date(iso).toLocaleString()}`;
  const repoOk = $derived(/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(repo.trim()));
  const canForward = $derived(repoOk && title.trim().length > 0 && !forwarding);
</script>

<header class="top">
  <div>
    <div class="eyebrow">Operators · feedback</div>
    <h2>{id === undefined ? 'Feedback reports' : `Feedback #${id}`}</h2>
  </div>
</header>

<LoadingGate
  phase={session.user?.operator && loading ? 'loading' : 'ready'}
  view={oneStep(id === undefined ? 'Feedback reports' : `Feedback #${id}`, id === undefined ? 'Reports' : 'Report', 'loading')}
>
{#if !session.user?.operator}
  <div class="card"><p class="muted">Feedback reports are for Changeling’s operators.</p></div>
{:else if error}
  <div class="card"><p class="negative" role="alert">{error}</p></div>
{:else if id === undefined}
  {#if !reports.length}
    <div class="card"><p class="muted">No feedback yet. Reports are kept for 90 days.</p></div>
  {:else}
    <ul class="reports">
      {#each reports as r (r.id)}
        <li>
          <a class="report-row card" href="/feedback/{r.id}">
            <span class="head">
              <b>#{r.id}</b>
              <span class="muted">{r.reporter.login ?? `user ${r.reporter.id}`} · {relTime(r.created_at)}</span>
              {#if r.forwarded}<span class="pill">forwarded</span>{/if}
            </span>
            <span class="excerpt">{r.excerpt}</span>
            <span class="meta muted">
              <code>{r.route}</code>
              {#if r.has_screenshot}<span class="pill neutral">screenshot</span>{/if}
              {#if r.has_logs}<span class="pill neutral">logs</span>{/if}
              {#if r.has_element}<span class="pill neutral">element</span>{/if}
            </span>
          </a>
        </li>
      {/each}
    </ul>
    {#if more}<button class="btn" onclick={() => loadList(reports[reports.length - 1]?.id)}>Older reports</button>{/if}
  {/if}
{:else if report}
  <p class="back"><a href="/feedback">← All reports</a></p>
  <div class="detail">
    <section class="card">
      <div class="eyebrow">From {report.reporter.login ?? `user ${report.reporter.id}`} (GitHub ID {report.reporter.id}) · {when(report.created_at)}</div>
      <p class="note">{report.note}</p>
      <dl class="facts">
        <dt>Page</dt>
        <dd><code>{report.route}</code></dd>
        <dt>Client</dt>
        <dd><code>{report.client_version}</code></dd>
        <dt>Server</dt>
        <dd><code>{report.server_version}</code></dd>
        <dt>Browser</dt>
        <dd class="small">{report.browser}</dd>
        <dt>Element</dt>
        <dd>
          {#if report.element}<code>{report.element.selector}</code>{#if report.element.text}<div class="small">“{report.element.text}”</div>{/if}
          {:else}<span class="muted">None</span>{/if}
        </dd>
      </dl>
    </section>

    <section class="card">
      <h3>Screenshot</h3>
      {#if shot}
        <a href={shot} target="_blank" rel="noreferrer"><img class="shot" src={shot} alt="The reporter’s screenshot" /></a>
      {:else if report.screenshot}<p class="muted">Loading the screenshot…</p>
      {:else}<p class="muted">None attached.</p>{/if}
    </section>

    <section class="card">
      <h3>App logs</h3>
      {#if report.logs?.length}
        <ol class="loglines">
          {#each report.logs as l, i (i)}<li><span class="at">{l.at.slice(11, 19)}</span> <span class="lvl {l.level}">{l.level}</span> {l.text}</li>{/each}
        </ol>
      {:else}<p class="muted">None attached.</p>{/if}
    </section>

    <section class="card">
      <h3>Forward to a GitHub issue</h3>
      {#if report.forwarded}
        <p>Forwarded to <a href={report.forwarded.url} target="_blank" rel="noreferrer">{report.forwarded.url}</a>.</p>
      {:else}
        <form class="forward" onsubmit={forward}>
          <label>Repository <input type="text" bind:value={repo} placeholder="owner/name" autocomplete="off" spellcheck="false" /></label>
          <label class="check">
            <input
              type="checkbox"
              bind:checked={linkShot}
              disabled={!shotLine}
              onchange={() => (body = toggleLine(body, shotLine, linkShot))}
            />
            Link the screenshot (it stays here: GitHub’s API takes no images, and only Changeling’s operators can open the link)
          </label>
          <label>Title <input type="text" bind:value={title} maxlength="256" /></label>
          <label>Body, exactly as the issue will read <textarea class="mono" rows="12" bind:value={body}></textarea></label>
          <p class="muted small">
            The draft holds the reporter’s note in a code block (so no mention, reference or link in it works), without links,
            references to other repositories or tokens, and the page as a pattern without names; never the reporter, the
            logs or the element. Anything you add goes to GitHub as you write it.
          </p>
          {#if forwardError}<p class="negative" role="alert">{forwardError}</p>{/if}
          <button class="btn primary" disabled={!canForward}>{forwarding ? 'Creating the issue…' : 'Create the issue'}</button>
        </form>
      {/if}
    </section>
  </div>
{/if}
</LoadingGate>

<style>
  .top h2 {
    margin-top: 4px;
  }
  .reports {
    list-style: none;
    margin: 0 0 16px;
    padding: 0;
    display: grid;
    gap: 10px;
  }
  .report-row {
    display: grid;
    gap: 6px;
    padding: 14px 18px;
    color: inherit;
    text-decoration: none;
  }
  .report-row:hover {
    border-color: var(--accent);
  }
  .head {
    display: flex;
    gap: 10px;
    align-items: baseline;
    flex-wrap: wrap;
  }
  .excerpt {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .meta {
    display: flex;
    gap: 6px;
    flex-wrap: wrap;
    align-items: center;
    font-size: 12px;
    min-width: 0;
  }
  .meta code {
    overflow-wrap: anywhere;
  }
  .back {
    margin-bottom: 14px;
  }
  .detail {
    display: grid;
    gap: 14px;
  }
  .detail .card {
    padding: 18px 20px;
    min-width: 0;
  }
  .detail h3 {
    margin-bottom: 10px;
  }
  .note {
    white-space: pre-wrap;
    margin: 10px 0 14px;
    font-size: 15px;
    overflow-wrap: anywhere;
  }
  .facts {
    display: grid;
    grid-template-columns: 80px 1fr;
    gap: 6px 12px;
    margin: 0;
    font-size: 13px;
  }
  .facts dt {
    color: var(--muted);
  }
  .facts dd {
    margin: 0;
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .small {
    font-size: 12px;
  }
  .shot {
    display: block;
    max-width: 100%;
    border: 1px solid var(--line);
    border-radius: 8px;
  }
  .loglines {
    margin: 0;
    padding: 8px 10px;
    list-style: none;
    font: 12px/1.6 ui-monospace, SFMono-Regular, Consolas, monospace;
    background: var(--code);
    border-radius: 6px;
    max-height: 320px;
    overflow: auto;
    overflow-wrap: anywhere;
  }
  .at,
  .lvl {
    color: var(--muted);
  }
  .lvl.error,
  .lvl.request {
    color: var(--bad);
  }
  .lvl.warn {
    color: var(--warn);
  }
  .forward {
    display: grid;
    gap: 10px;
    max-width: 720px;
  }
  .forward label {
    display: grid;
    gap: 4px;
    font-size: 13px;
  }
  .forward .check {
    display: flex;
    align-items: center;
    gap: 8px;
  }
  .forward p {
    margin: 0;
  }
  .forward textarea {
    font-size: 12px;
    min-height: 220px;
  }
  .forward button {
    justify-self: start;
  }
  @media (max-width: 500px) {
    .facts {
      grid-template-columns: 1fr;
    }
    .facts dt {
      margin-top: 6px;
    }
  }
</style>
